import { getAccessToken } from './authToken';

// Simple API response type
export interface ApiResponse<T = unknown> {
  data: T;
  message?: string;
  success?: boolean;
}

interface ApiClientError extends Error {
  status: number;
  data?: unknown;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

// Simple error function
export const createApiError = (
  message: string,
  status: number = 0,
  data?: unknown
): ApiClientError => {
  const error = new Error(message) as ApiClientError;
  error.status = status;
  error.data = data;
  error.name = 'ApiError';
  return error;
};

// Simple fetcher function
export const apiClient = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  const baseURL = process.env['REACT_APP_API_URL'] || 'http://localhost:8000';
  const url = `${baseURL}${endpoint}`;

  // Get token automatically from localStorage
  const token = getAccessToken();

  // Build headers
  const headers = new Headers(options.headers);

  if (
    !headers.has('Content-Type') &&
    !(options.body instanceof FormData) &&
    !(options.body instanceof URLSearchParams)
  ) {
    headers.set('Content-Type', 'application/json');
  }

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      // We send JWT via Authorization header; do not send cookies/credentials to avoid credentialed CORS
      credentials: 'omit',
    });
    
    // Handle 401 - redirect to login (but not for login/register endpoints)
    if (
      response.status === 401 &&
      !endpoint.includes('/auth/login') &&
      !endpoint.includes('/auth/register')
    ) {
      // Clear the persisted session first, otherwise PublicRoute sees a stale
      // isAuthenticated=true and immediately bounces back to the dashboard.
      try {
        localStorage.removeItem('persist:auth');
      } catch {
        // ignore storage errors
      }
      window.location.href = '/login';
      throw createApiError('Session expired. Please log in again.', 401);
    }

    // Parse response data
    let data: unknown;
    try {
      const responseText = await response.text();

      if (responseText) {
        data = JSON.parse(responseText);
      } else {
        data = {};
      }
    } catch (parseError) {
      throw createApiError(
        `Request failed with status ${response.status}`,
        response.status,
        { message: `HTTP ${response.status} ${response.statusText}` }
      );
    }

    if (!response.ok) {
      let errorMessage = 'Request failed';

      if (isRecord(data)) {
        if (typeof data['detail'] === 'string') {
          errorMessage = data['detail'];
        } else if (typeof data['message'] === 'string') {
          errorMessage = data['message'];
        } else if (typeof data['error'] === 'string') {
          errorMessage = data['error'];
        } else if (Array.isArray(data['detail'])) {
          errorMessage = data['detail']
            .map((err: unknown) => {
              if (!isRecord(err)) return 'Validation error';
              const message = err['msg'] ?? err['message'];
              return typeof message === 'string'
                ? message
                : 'Validation error';
            })
            .join(', ');
        }
      }

      if (errorMessage === 'Request failed') {
        if (response.status === 401) {
          errorMessage = 'Incorrect username or password';
        } else if (response.status === 400) {
          errorMessage = 'Invalid request';
        } else if (response.status === 500) {
          errorMessage = 'Server error';
        } else {
          errorMessage = `Request failed with status ${response.status}`;
        }
      }

      throw createApiError(errorMessage, response.status, data);
    }

    return data as T;
  } catch (error) {
    if (error instanceof Error && error.name === 'ApiError') {
      throw error;
    }
    throw createApiError(
      error instanceof Error ? error.message : 'Network error'
    );
  }
};

// HTTP method helpers
export const get = <T>(endpoint: string): Promise<T> =>
  apiClient<T>(endpoint, { method: 'GET' });

export const post = <T>(endpoint: string, data?: unknown): Promise<T> =>
  apiClient<T>(endpoint, {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  });

// Special function for auth endpoints that require form-encoded data
export const postForm = <T>(
  endpoint: string,
  data?: Record<string, unknown>
): Promise<T> => {
  const formData = new URLSearchParams();
  if (data) {
    Object.entries(data).forEach(([key, value]) => {
      formData.append(key, String(value));
    });
  }
  return apiClient<T>(endpoint, {
    method: 'POST',
    body: formData,
  });
};

export const put = <T>(endpoint: string, data?: unknown): Promise<T> =>
  apiClient<T>(endpoint, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  });

export const del = <T>(endpoint: string): Promise<T> =>
  apiClient<T>(endpoint, { method: 'DELETE' });
